import { CalendarDate, getLocalTimeZone, today } from '@internationalized/date';
import { Maybe, rd } from '@passionware/monads';
import { TFunction } from 'i18next';
import { Calendar } from 'lucide-react';
import {
  createDateFilter,
  dateFilterSchemaOf,
  GenericDateFilter
} from 'v5/api/_common/query/filters/GenericDateFilter';
import { FilterDateConfig } from 'v5/patterns/filters/FilterWidget.types';
import { convertDateFilterToApi } from 'v5/platform/filters/filterConverters';

const customFilterPresetIds = [
  'last7Days',
  'last30Days',
  'last90Days',
  'today',
  'tomorrow',
  'nextWeek',
  'nextMonth'
] as const;

export type CustomFilterPreset = (typeof customFilterPresetIds)[number];

export type CustomFilter = GenericDateFilter<CustomFilterPreset>;

function resolveRange(
  preset: CustomFilterPreset,
  today: CalendarDate
): {
  from: CalendarDate;
  to: CalendarDate;
} {
  switch (preset) {
    case 'last7Days':
      return {
        from: today.subtract({ days: 6 }),
        to: today
      };
    case 'last30Days':
      return {
        from: today.subtract({ days: 29 }),
        to: today
      };
    case 'last90Days':
      return {
        from: today.subtract({ days: 89 }),
        to: today
      };
    case 'today':
      return {
        from: today,
        to: today
      };
    case 'tomorrow':
      return {
        from: today.add({ days: 1 }),
        to: today.add({ days: 1 })
      };
    case 'nextWeek':
      const nextWeekStart = today
        .add({ weeks: 1 })
        .subtract({ days: today.day - 1 });
      const nextWeekEnd = nextWeekStart.add({ days: 6 });
      return {
        from: nextWeekStart,
        to: nextWeekEnd
      };
    case 'nextMonth':
      const nextMonth = today.add({ months: 1 });
      const nextMonthStart = nextMonth.subtract({ days: nextMonth.day - 1 });
      const nextMonthEnd = nextMonthStart
        .add({ months: 1 })
        .subtract({ days: 1 });
      return {
        from: nextMonthStart,
        to: nextMonthEnd
      };
  }
}

const createRangeMatcher =
  (preset: CustomFilterPreset) => (date: CalendarDate, now: CalendarDate) => {
    const { from, to } = resolveRange(preset, now);
    return date.compare(from) >= 0 && date.compare(to) <= 0;
  };

function createCustomFilter(
  label: string,
  t: TFunction<'core'>
): FilterDateConfig<CustomFilterPreset> {
  return {
    label,
    icon: <Calendar />,
    type: 'date',
    createDefaultValue: () => ({ operator: 'preset', value: 'today' }),
    options: {
      useOptions() {
        return rd.of(
          customFilterPresetIds.map(preset => ({
            value: preset,
            label: t(`standard-date-filter.custom.${preset}`)
          }))
        );
      },
      useResolver() {
        return rd.of(
          createDateFilter(
            Object.fromEntries(
              customFilterPresetIds.map(preset => [
                preset,
                {
                  match: createRangeMatcher(preset)
                }
              ])
            )
          )
        );
      }
    }
  };
}

function createCustomHttpConverter() {
  return (filter: Maybe<GenericDateFilter<never>>) => {
    return filter
      ? convertDateFilterToApi<CustomFilterPreset>(filter, {
          resolvePreset: preset =>
            resolveRange(preset, today(getLocalTimeZone()))
        })
      : null;
  };
}

const customFilterSchema = dateFilterSchemaOf([
  'last7Days',
  'last30Days',
  'last90Days',
  'today',
  'tomorrow',
  'nextWeek',
  'nextMonth'
]);

export const customFilter = {
  createFilterConfig: createCustomFilter,
  createHttpConverter: createCustomHttpConverter,
  resolveRange: resolveRange,
  schema: customFilterSchema
};
